// src/utils/formatters.ts
import { Trip, TouchPoint } from '../models/types';

/**
 * Format weight with appropriate unit (kg or T)
 */
export const formatWeight = (weightKg: number): string => {
  if (weightKg >= 1000) {
    return `${(weightKg / 1000).toFixed(0)} T`;
  }
  return `${weightKg.toFixed(0)} kg`;
};

/**
 * Format distance with km unit
 */
export const formatDistance = (distanceKm: number): string => {
  return `${Math.round(distanceKm)} km`;
};

/**
 * Format volume in cubic feet
 */
export const formatVolume = (volumeCuFt: number): string => {
  return `${Math.round(volumeCuFt)} cu ft`;
};

/**
 * Format percentage value
 */
export const formatPercentage = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * Format cost value with currency symbol
 */
export const formatCost = (cost: number): string => {
  return `₹${cost.toFixed(2)}`;
};

/**
 * Format epoch timestamp to readable time
 */
export const formatEpochTime = (epoch: number): string => {
  const date = new Date(epoch * 1000);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Format minutes of day to hours:minutes format
 */
export const formatMinutesOfDay = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Format duration given minutes to "Hh Mm" (omits zero parts where possible)
 */
export const formatDurationFromMinutes = (minutes: number): string => {
  const totalMinutes = Math.max(0, Math.floor(minutes));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

/**
 * Format duration given seconds to "Hh Mm" (omits zero parts where possible)
 */
export const formatDurationFromSeconds = (seconds: number): string => {
  const totalMinutes = Math.max(0, Math.floor(seconds / 60));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

export const formatKg = (weightKg: number): string => `${Math.round(weightKg)} kg`;

export const formatHoursMinutes = (minutes: number, showPlus = false): string => {
  const totalMinutes = Math.max(0, Math.round(Math.abs(minutes)));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${showPlus ? '+' : ''}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

interface TripWindowMetrics {
  startMinutes: number | null;
  endMinutes: number | null;
  durationMinutes: number | null;
  startLabel: string;
  endLabel: string;
  windowLabel: string;
  durationLabel: string;
}

export interface TripStopMetrics {
  pickups: number;
  drops: number;
  totalPickKg: number;
  totalDropKg: number;
}

export interface TripDeltaMetrics {
  used: number;
  limit: number;
  delta: number;
  isOver: boolean;
}

const includesToken = (source: string, token: string): boolean => source.includes(token);

const classifyTouchPoint = (touchPoint: TouchPoint): 'origin' | 'pickup' | 'drop' | 'both' => {
  const tags = (touchPoint.slotTags ?? []).map((tag) => String(tag).toUpperCase());
  const nameUpper = String(touchPoint.name ?? '').toUpperCase();

  const hasOrigin = tags.some((tag) => includesToken(tag, 'ORIGIN')) ||
    includesToken(nameUpper, 'ORIGIN') ||
    includesToken(nameUpper, 'DEPOT') ||
    includesToken(nameUpper, 'HUB');
  if (hasOrigin) {
    return 'origin';
  }

  const hasPickupTag = tags.some((tag) => includesToken(tag, 'PICK'));
  const hasDropTag = tags.some((tag) => includesToken(tag, 'DROP') || includesToken(tag, 'DELIVER'));
  const pickWt = Number(touchPoint.itemSummary?.pickWtKg ?? 0);
  const dropWt = Number(touchPoint.itemSummary?.dropWtKg ?? 0);
  const hasPickWeight = Number.isFinite(pickWt) && pickWt > 0;
  const hasDropWeight = Number.isFinite(dropWt) && dropWt > 0;

  if ((hasPickupTag && hasDropTag) || (hasPickWeight && hasDropWeight)) {
    return 'both';
  }
  if (hasPickupTag || hasPickWeight || includesToken(nameUpper, 'PICK')) {
    return 'pickup';
  }
  if (
    hasDropTag ||
    hasDropWeight ||
    includesToken(nameUpper, 'DROP') ||
    includesToken(nameUpper, 'DELIVER')
  ) {
    return 'drop';
  }

  // Fallback for delivery-only datasets without explicit slot tags.
  return 'drop';
};

export const getTripStopMetrics = (trip: Trip): TripStopMetrics => {
  return trip.tpDetails.reduce<TripStopMetrics>(
    (acc, touchPoint) => {
      const type = classifyTouchPoint(touchPoint);
      if (type === 'pickup' || type === 'both') acc.pickups += 1;
      if (type === 'drop' || type === 'both') acc.drops += 1;
      acc.totalPickKg += Math.max(0, Number(touchPoint.itemSummary?.pickWtKg ?? 0));
      acc.totalDropKg += Math.max(0, Number(touchPoint.itemSummary?.dropWtKg ?? 0));
      return acc;
    },
    { pickups: 0, drops: 0, totalPickKg: 0, totalDropKg: 0 }
  );
};

const toMinutesOfDay = (value: unknown): number | null => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  // Already minutes-of-day.
  if (n <= 1440) return Math.round(n);
  // Seconds-of-day.
  if (n <= 86400) return Math.round(n / 60);
  // Epoch seconds.
  const date = new Date(n * 1000);
  if (!Number.isFinite(date.getTime())) return null;
  return date.getHours() * 60 + date.getMinutes();
};

const parseTimeTokenToMinutes = (raw: string): number | null => {
  const value = raw.trim();
  if (!value) return null;
  const meridiemMatch = value.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (meridiemMatch) {
    let hours = Number(meridiemMatch[1]) % 12;
    const minutes = Number(meridiemMatch[2]);
    const meridiem = meridiemMatch[3].toUpperCase();
    if (meridiem === 'PM') hours += 12;
    return hours * 60 + minutes;
  }
  const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeMatch) {
    return (Number(timeMatch[1]) * 60) + Number(timeMatch[2]);
  }
  return null;
};

const parseWorkingTimeToMinutes = (raw: unknown): number | null => {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length < 2) return null;
  const start = parseTimeTokenToMinutes(parts[0]);
  const end = parseTimeTokenToMinutes(parts[1]);
  if (start == null || end == null) return null;
  return end >= start ? end - start : (end + 1440) - start;
};

const toPositiveNumber = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const getTripTotalQtyKg = (trip: Trip): number => {
  const tripWeight = toPositiveNumber(trip.totalWtKg);
  if (tripWeight != null) {
    return tripWeight;
  }
  const stopMetrics = getTripStopMetrics(trip);
  return Math.max(0, stopMetrics.totalPickKg + stopMetrics.totalDropKg);
};

export const getTripCapacityDelta = (trip: Trip): TripDeltaMetrics | null => {
  const used = getTripTotalQtyKg(trip);
  const limit = toPositiveNumber(trip.vehDetails.capacityKg);
  if (limit == null) {
    return null;
  }
  const delta = limit - used;
  return {
    used,
    limit,
    delta,
    isOver: delta < 0,
  };
};

export const getTripWorkingTimeDelta = (trip: Trip): TripDeltaMetrics | null => {
  const duration = getTripWindowMetrics(trip).durationMinutes;
  const used = toPositiveNumber(duration);
  const limit = toPositiveNumber(trip.vehDetails.workingMinutes)
    ?? parseWorkingTimeToMinutes(trip.vehDetails.workingTime);
  if (used == null || limit == null) {
    return null;
  }
  const delta = limit - used;
  return {
    used,
    limit,
    delta,
    isOver: delta < 0,
  };
};

export const getTripWindowMetrics = (trip: Trip): TripWindowMetrics => {
  const starts: number[] = [];
  const ends: number[] = [];

  trip.tpDetails.forEach((tp) => {
    const attemptStart = toMinutesOfDay(tp.attemptTime?.start);
    const attemptEnd = toMinutesOfDay(tp.attemptTime?.end);
    if (attemptStart != null) starts.push(attemptStart);
    if (attemptEnd != null) ends.push(attemptEnd);
  });

  if (!starts.length || !ends.length) {
    trip.tpDetails.forEach((tp) => {
      const window = tp.openHoursMinOfDay?.[0];
      const windowStart = toMinutesOfDay(window?.start);
      const windowEnd = toMinutesOfDay(window?.end);
      if (windowStart != null) starts.push(windowStart);
      if (windowEnd != null) ends.push(windowEnd);
    });
  }

  if ((!starts.length || !ends.length) && trip.assignmentMeta) {
    const fallbackStart = toMinutesOfDay(trip.assignmentMeta.originStartMinutes);
    const fallbackDuration = Number(trip.assignmentMeta.tripDurationMinutes);
    if (fallbackStart != null) {
      starts.push(fallbackStart);
      if (Number.isFinite(fallbackDuration) && fallbackDuration > 0) {
        ends.push(fallbackStart + Math.round(fallbackDuration));
      }
    }
  }

  if (!starts.length || !ends.length) {
    return {
      startMinutes: null,
      endMinutes: null,
      durationMinutes: null,
      startLabel: '—',
      endLabel: '—',
      windowLabel: '—',
      durationLabel: '—',
    };
  }

  const start = Math.min(...starts);
  const endRaw = Math.max(...ends);
  const end = endRaw >= start ? endRaw : endRaw + 1440;
  const duration = Math.max(0, end - start);
  const startLabel = formatMinutesOfDay(start % 1440);
  const endLabel = formatMinutesOfDay(end % 1440);

  return {
    startMinutes: start,
    endMinutes: end,
    durationMinutes: duration,
    startLabel,
    endLabel,
    windowLabel: `${startLabel} - ${endLabel}`,
    durationLabel: formatDurationFromMinutes(duration),
  };
};

/**
 * Get display status for a trip based on breaches
 */
export const getTripStatus = (trip: Trip): 'normal' | 'warning' | 'error' => {
  const hasWeightBreach = trip.breachesAndRatios.wtBreachKg > 0;
  const hasVolumeBreach = trip.breachesAndRatios.volBreachCuFt > 0;
  const hasDistanceBreach = trip.breachesAndRatios.distBreachKm > 0;
  const hasTimeBreach = trip.breachesAndRatios.timeBreachMin > 0;
  const hasSlotBreach = trip.breachesAndRatios.slotBreaches > 0;
  
  if (hasWeightBreach || hasVolumeBreach || hasDistanceBreach || 
      (hasTimeBreach && trip.breachesAndRatios.timeBreachMin > 10) || hasSlotBreach) {
    return 'error';
  }
  
  if (hasTimeBreach) {
    return 'warning';
  }
  
  return 'normal';
};

/**
 * Generate summary text for a touch point
 */
export const getTouchPointSummary = (touchPoint: TouchPoint): string => {
  const items = [];
  
  if (touchPoint.itemSummary.dropWtKg > 0) {
    items.push(`Drop: ${formatWeight(touchPoint.itemSummary.dropWtKg)}`);
  }
  
  if (touchPoint.itemSummary.pickWtKg > 0) {
    items.push(`Pick: ${formatWeight(touchPoint.itemSummary.pickWtKg)}`);
  }
  
  return items.join(', ');
};

/**
 * Helper to calculate the total weight of selected touch points
 */
export const calculateTotalWeight = (touchPoints: TouchPoint[]): number => {
  return touchPoints.reduce(
    (sum, tp) => sum + tp.itemSummary.dropWtKg + tp.itemSummary.pickWtKg, 
    0
  );
};

/**
 * Helper to check if moving touch points would cause a weight breach
 */
export const wouldCauseWeightBreach = (
  destinationTrip: Trip,
  touchPoints: TouchPoint[],
  weightLimit: number
): boolean => {
  const currentWeight = destinationTrip.totalWtKg;
  const additionalWeight = calculateTotalWeight(touchPoints);
  
  return (currentWeight + additionalWeight) > weightLimit;
};
